import type { ReactNode } from 'react';
import { useIsMobile } from '@hooks/useIsMobile';

/**
 * Conditionally renders either the existing ag-grid view (desktop) or a
 * vertically-stacked card list (mobile) based on viewport width.
 *
 * Use this as a wrapper around each feature's existing grid component so
 * the desktop UX stays identical and the mobile UX gets a vertical card
 * list designed for portrait phones — no horizontal scroll, no squashed
 * cells.
 *
 * Per-screen contract:
 * - Pass the existing `<FooGrid />` JSX as `desktop`. Untouched on md+.
 * - Pass a per-feature `renderCard(row)` for the mobile path. The card is
 *   designed by the feature — generic auto-layout would produce bland,
 *   uniform cards regardless of the data.
 *
 * Fallback: if `renderCard` is omitted, the desktop view renders at every
 * viewport (ag-grid horizontal-scroll). Lets us migrate screens
 * incrementally without ResponsiveDataList becoming a hard dependency.
 *
 * Empty / loading / error states are handled BY THE PARENT SCREEN — this
 * component renders nothing when `rows` is empty, deferring to the
 * screen's empty-state UI.
 */

interface Props<T> {
  rows: readonly T[];
  getRowId: (row: T) => string;
  /** Desktop view — the existing `<FooGrid />` JSX. */
  desktop: ReactNode;
  /** Mobile-only card renderer. Omit to keep ag-grid at all sizes. */
  renderCard?: (row: T) => ReactNode;
}

export function ResponsiveDataList<T>({
  rows,
  getRowId,
  desktop,
  renderCard,
}: Props<T>) {
  const isMobile = useIsMobile();

  if (isMobile && renderCard) {
    return (
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
        {rows.map((row) => (
          <div key={getRowId(row)}>{renderCard(row)}</div>
        ))}
      </div>
    );
  }

  return <>{desktop}</>;
}
